import {apiClient} from '@common/http/query-client';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  GadAnalytics,
  GadApiKey,
  GadApiReference,
  GadApiUsageLog,
  GadCatalogOwnership,
  GadCatalogType,
  GadDocument,
  GadPagination,
  GadPartnerAccount,
  GadReportDefinition,
  GadSubmission,
  GadSummary,
  GadTeamMember,
  GadWebhook,
} from './types';

export function useGadMe() {
  return useQuery({
    queryKey: ['gad', 'me'],
    queryFn: () =>
      apiClient
        .get<{partner_account: GadPartnerAccount | null}>('gad/me')
        .then(r => r.data.partner_account),
  });
}

export function useGadSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'summary'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{summary: GadSummary}>('gad/summary')
        .then(r => r.data.summary),
  });
}

export function useGadAnalytics(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'analytics'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{analytics: GadAnalytics}>('gad/analytics')
        .then(r => r.data.analytics),
  });
}

export function useGadUploads(enabled: boolean, params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['gad', 'uploads', params],
    enabled,
    queryFn: () =>
      apiClient
        .get<{
          submissions: GadPagination<GadSubmission>;
          documents: GadDocument[];
        }>('gad/uploads', {params})
        .then(r => r.data),
  });
}

export function useGadCatalog(enabled: boolean, type: GadCatalogType) {
  return useQuery({
    queryKey: ['gad', 'catalog', type],
    enabled,
    queryFn: () =>
      apiClient
        .get<{
          ownerships: GadPagination<GadCatalogOwnership>;
          summary: {artists: number; albums: number; tracks: number};
        }>('gad/catalog', {params: {type, per_page: 50}})
        .then(r => r.data),
  });
}

export function useGadReports(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'reports'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{reports: GadReportDefinition[]; stats: Record<string, number>}>(
          'gad/reports',
        )
        .then(r => r.data),
  });
}

export function useGenerateGadReport() {
  return useMutation({
    mutationFn: (payload: {type: string; format?: string; period_days?: number}) =>
      apiClient.post<{report: Record<string, unknown>}>('gad/reports', payload).then(r => r.data.report),
  });
}

export function useGadFinance(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'finance'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{
          pagination: GadPagination<GadCatalogOwnership>;
          summary: Record<string, number>;
        }>('gad/finance')
        .then(r => r.data),
  });
}

export function useGadWebhooks(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'webhooks'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{
          webhooks: GadWebhook[];
          events: string[];
          documentation: Record<string, unknown>;
        }>('gad/webhooks')
        .then(r => r.data),
  });
}

export function useCreateGadWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {url: string; events: string[]; status: string}) =>
      apiClient.post<{webhook: GadWebhook; signing_secret: string}>('gad/webhooks', payload).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'webhooks']}),
  });
}

export function useUpdateGadWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, payload}: {id: number; payload: Partial<GadWebhook> & {rotate_secret?: boolean}}) =>
      apiClient.patch<{webhook: GadWebhook; signing_secret?: string | null}>(`gad/webhooks/${id}`, payload).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'webhooks']}),
  });
}

export function useDeleteGadWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`gad/webhooks/${id}`).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'webhooks']}),
  });
}

export function useGadTeam(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'team-members'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{team_members: GadTeamMember[]; roles: Record<string, string>}>(
          'gad/team-members',
        )
        .then(r => r.data),
  });
}

export function useCreateGadTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {email: string; name?: string; role: string; status?: string}) =>
      apiClient.post<{team_member: GadTeamMember}>('gad/team-members', payload).then(r => r.data.team_member),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'team-members']}),
  });
}

export function useUpdateGadTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, payload}: {id: number; payload: Partial<GadTeamMember>}) =>
      apiClient.patch<{team_member: GadTeamMember}>(`gad/team-members/${id}`, payload).then(r => r.data.team_member),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'team-members']}),
  });
}

export function useDeleteGadTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`gad/team-members/${id}`).then(r => r.data),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['gad', 'team-members']}),
  });
}

export function useGadApiKeys(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'api-keys'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{api_keys: GadApiKey[]}>('gad/api-keys')
        .then(r => r.data.api_keys),
  });
}

export function useGadApiReference(enabled: boolean) {
  return useQuery({
    queryKey: ['gad', 'api-reference'],
    enabled,
    queryFn: () =>
      apiClient
        .get<{api_reference: GadApiReference}>('gad/api-reference')
        .then(r => r.data.api_reference),
  });
}

export function useGadApiLogs(enabled: boolean, params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['gad', 'api-usage', params],
    enabled,
    queryFn: () =>
      apiClient
        .get<{pagination: GadPagination<GadApiUsageLog>}>('gad/api-usage', {
          params,
        })
        .then(r => r.data.pagination),
  });
}
